import {Injectable} from '@angular/core';
import {Blob as CBLBlob, type Collection, Database, type DatabaseConfig, DocID, LastWriteWins, NewBlob} from '@couchbase/lite-js';
import {ImageFile} from '../models/training-audit/image-file.model';
import {DraftEntry, SavedImageEntry} from '../models/cb-lite/db-model';
import {DBSchema, DraftMeta, DraftSchema, ImageSchema} from '../models/cb-lite/db-schema';
import {LogLevel} from '@grafana/faro-web-sdk';
import {TelemetryService} from './telemetry.service';
import {TelemetryEvent} from '../enum/telemetry-event.enum';

const dbConfig: DatabaseConfig<DBSchema> = {
  name: 'myteam',
  version: 2,
  collections: {
    drafts: { indexes: [] },
    images: { indexes: ['draftKey'] },
    pending_photos: { indexes: ['status'] }
  },
}

const SLOW_WRITE_MS = 5_000;
const QUEUE_STALL_MS = 30_000;
const DB_OPEN_TIMEOUT_MS = 10_000;

interface PendingImage {
  questionId: string;
  fileName: string;
  mimeType: string;
  file: File;
  res?: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class OfflineService {
  private _dbPromise: Promise<Database<DBSchema>> | null = null;
  private _queue: Promise<unknown> = Promise.resolve();
  private _opening: Promise<Database<DBSchema>> | null = null;
  private readonly _abandoned = new WeakSet<Database<DBSchema>>();
  private _storageLost = false;
  private _storageBlocked = false;
  private _cleanedFor: string | null = null;
  private _connectionClosed: Promise<void> = new Promise<void>(() => undefined);
  private _markConnectionClosed: (() => void) | null = null;
  private _hidden = false;
  private _visibilityChanges = 0;
  private _visible: Promise<void> = Promise.resolve();
  private _markVisible: (() => void) | null = null;
  private readonly _activeOps = new Set<object>();
  private readonly DRAFT_PREFIX = 'draft_';

  constructor(private readonly telemetry: TelemetryService) {
    document.addEventListener('visibilitychange', () => document.hidden ? this.onHidden() : this.onVisible());
    window.addEventListener('pagehide', () => this.onHidden());
    window.addEventListener('pageshow', () => this.onVisible());
    if (document.hidden) {
      this.onHidden();
    }
  }

  private onHidden(): void {
    if (!this._hidden) {
      this._hidden = true;
      this._visibilityChanges++;
      this._visible = new Promise<void>(resolve => {
        this._markVisible = resolve;
      });
      if (this._activeOps.size > 0) {
        this.telemetry.event(TelemetryEvent.OFFLINE_PAGE_HIDDEN, LogLevel.INFO, {
          dbOpen: !!this._dbPromise,
          activeOps: this._activeOps.size,
          storageBlocked: this._storageBlocked
        });
      }
    }
    this.closeIfHiddenAndIdle();
  }

  private onVisible(): void {
    if (!this._hidden || document.hidden) {
      return;
    }
    this._hidden = false;
    this._visibilityChanges++;
    this._storageBlocked = false;
    this._markVisible?.();
    this._markVisible = null;
    this.db().then(() => this.closeIfHiddenAndIdle(), (): void => undefined);
  }

  private closeIfHiddenAndIdle(): void {
    if (!this._hidden || this._activeOps.size > 0 || this._opening || !this._dbPromise) {
      return;
    }
    const dbPromise = this._dbPromise;
    this._dbPromise = null;
    dbPromise.then(db => this.closeAbandoned(db), (): void => undefined);
  }

  private releaseOp(activeOp: object): void {
    this._activeOps.delete(activeOp);
    this.closeIfHiddenAndIdle();
  }

  private run<T>(op: (db: Database<DBSchema>) => Promise<T>): Promise<T> {
    const activeOp = {};
    const started = this._queue
      .then(() => this._visible)
      .then(() => {
        this._activeOps.add(activeOp);
        return this.db();
      });
    const result = started.then(db => op(db)).catch(err => {
      throw this.isStorageLost(err) ? this.loseStorage(err) : err;
    }).finally(() => this.releaseOp(activeOp));
    this._queue = started.then(
      () => this.advanceQueueWhenSettledOrConnectionLost(result, this._connectionClosed, this._dbPromise, activeOp),
      (): void => undefined
    );
    return result;
  }

  private async runRead<T>(operation: string, op: (db: Database<DBSchema>) => Promise<T>): Promise<T> {
    try {
      return await this.run(op);
    } catch (err) {
      if (!(err as any)?.telemetryReported) {
        this.telemetry.event(TelemetryEvent.OFFLINE_READ_FAILURE, LogLevel.ERROR, {
          operation,
          errorName: (err as any)?.name,
          error: (err as any)?.message ?? String(err),
        });
      }
      throw err;
    }
  }

  private async runWrite<T>(operation: string, op: (db: Database<DBSchema>) => Promise<T>): Promise<T> {
    const startedAt = Date.now();
    const visibilityChangesAtStart = this._visibilityChanges;
    try {
      const result = await this.run(op);
      const durationMs = Date.now() - startedAt;
      if (durationMs >= SLOW_WRITE_MS && this._visibilityChanges === visibilityChangesAtStart) {
        this.telemetry.event(TelemetryEvent.OFFLINE_SLOW_WRITE, LogLevel.WARN, {
          operation,
          durationMs,
          message: `Write took ${durationMs} ms including time waiting in the queue`
        });
      }
      return result;
    } catch (err) {
      if (!(err as any)?.telemetryReported) {
        this.telemetry.event(TelemetryEvent.OFFLINE_WRITE_FAILURE, LogLevel.ERROR, {
          operation,
          durationMs: Date.now() - startedAt,
          errorName: (err as any)?.name,
          error: (err as any)?.message ?? String(err),
          ...await this.storagePctUsed()
        });
      }
      throw err;
    }
  }

  private advanceQueueWhenSettledOrConnectionLost(
    result: Promise<unknown>,
    connectionClosed: Promise<void>,
    dbPromiseAtStart: Promise<Database<DBSchema>> | null,
    activeOp: object
  ): Promise<void> {
    return new Promise<void>(resolve => {
      let settled = false;
      const stallTimer = setTimeout(() => {
        if (settled) return;
        settled = true;
        this.telemetry.event(TelemetryEvent.OFFLINE_QUEUE_STALLED, LogLevel.WARN, {
          message: `A queued write did not settle within ${QUEUE_STALL_MS}ms and its connection was never reported closed; forcing a reconnect for later writes`
        });
        if (dbPromiseAtStart && this._dbPromise === dbPromiseAtStart) {
          this._dbPromise = null;
          dbPromiseAtStart.then(db => this.closeAbandoned(db), (): void => undefined);
        }
        this.releaseOp(activeOp);
        resolve();
      }, QUEUE_STALL_MS);
      const settle = () => {
        if (settled) return;
        settled = true;
        clearTimeout(stallTimer);
        resolve();
      };
      result.finally(settle).catch((): void => undefined);
      connectionClosed.then(settle);
    });
  }

  private closeAbandoned(db: Database<DBSchema>): void {
    this._abandoned.add(db);
    try {
      db.close();
    } catch {
    }
  }

  private renewConnectionClosedSignal(): void {
    this._connectionClosed = new Promise<void>(resolve => {
      this._markConnectionClosed = resolve;
    });
  }

  private db(): Promise<Database<DBSchema>> {
    if (this._storageLost) {
      return Promise.reject(this.storageLostError());
    }
    if (this._storageBlocked) {
      return Promise.reject(this.storageBlockedError());
    }
    this._opening ??= this.openDb().finally(() => {
      this._opening = null;
    });
    return this._opening;
  }

  private async openDb(): Promise<Database<DBSchema>> {
    if (this._dbPromise) {
      try {
        const db = await this._dbPromise;
        if (this.isUsable(db)) {
          return db;
        }
        await db.reopen();
        if (this.isUsable(db)) {
          this.renewConnectionClosedSignal();
          this.telemetry.event(TelemetryEvent.OFFLINE_DB_REOPENED, LogLevel.WARN, {
            message: `Database was closed and has been reopened`
          });
          return db;
        }
      } catch (err) {
        if (this.isStorageLost(err)) {
          throw this.loseStorage(err);
        }
        this.telemetry.event(TelemetryEvent.OFFLINE_DB_UNUSABLE, LogLevel.ERROR, {
          errorName: (err as any)?.name,
          error: `Database unusable, opening a new connection: ${(err as any)?.message ?? String(err)}`
        });
      }
      this._dbPromise = null;
    }

    const startedAt = Date.now();
    const opening = Database.open(dbConfig);
    this._dbPromise = this.withOpenTimeout(opening).then(
      db => {
        this.renewConnectionClosedSignal();
        this.telemetry.event(TelemetryEvent.OFFLINE_DB_OPENED, LogLevel.INFO, {
          version: dbConfig.version,
          durationMs: Date.now() - startedAt
        });
        db.addCloseListener((reason, newVersion) => {
          if (!this._abandoned.has(db)) {
            this.onClose(reason, newVersion);
          }
        });
        return db;
      },
      err => {
        this._dbPromise = null;
        if ((err as any)?.name === 'DbOpenTimeoutError') {
          this._storageBlocked = true;
          opening.then(
            db => {
              this._storageBlocked = false;
              this.closeAbandoned(db);
            },
            (): void => undefined
          );
        }
        throw this.isStorageLost(err) ? this.loseStorage(err) : err;
      }
    );
    return this._dbPromise;
  }

  private withOpenTimeout(opening: Promise<Database<DBSchema>>): Promise<Database<DBSchema>> {
    return new Promise<Database<DBSchema>>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.telemetry.event(TelemetryEvent.OFFLINE_DB_OPEN_TIMEOUT, LogLevel.WARN, {
          message: `Database did not open within ${DB_OPEN_TIMEOUT_MS / 1000}s; storage is blocked until the page is shown again`
        });
        const err = new Error(`Database did not open within ${DB_OPEN_TIMEOUT_MS / 1000}s`);
        err.name = 'DbOpenTimeoutError';
        reject(err);
      }, DB_OPEN_TIMEOUT_MS);
      opening.then(
        db => {
          clearTimeout(timer);
          resolve(db);
        },
        err => {
          clearTimeout(timer);
          reject(err);
        }
      );
    });
  }

  private onClose(reason: 'close' | 'delete' | 'upgrade', newVersion?: number): void {
    this._dbPromise = null;
    this._markConnectionClosed?.();

    if (reason === 'upgrade') {
      this.telemetry.event(TelemetryEvent.OFFLINE_DB_CLOSED, LogLevel.WARN, {
        reason,
        newVersion,
        message: `Another tab opened a newer database version; reloading the page`
      });
      window.location.reload();
      return;
    }

    this.telemetry.event(TelemetryEvent.OFFLINE_DB_CLOSED, LogLevel.WARN, {
      reason,
      message: reason === 'delete'
        ? `Database was deleted outside the app; queued drafts and photos are gone and it will reopen empty`
        : `Database connection was closed by the browser; the next write will try to reopen it`
    });
  }

  private isStorageLost(err: unknown): boolean {
    return /Indexed Database server lost/i.test((err as any)?.message ?? '');
  }

  private loseStorage(err: unknown): Error {
    if (!this._storageLost) {
      this._storageLost = true;
      this._dbPromise = null;
      this.telemetry.event(TelemetryEvent.OFFLINE_STORAGE_LOST, LogLevel.ERROR, {
        errorName: (err as any)?.name,
        error: `Local storage is unrecoverable until the page reloads: ${(err as any)?.message ?? String(err)}`
      });
    }
    return this.storageLostError();
  }

  private storageLostError(): Error {
    const err = new Error('Local storage is unavailable until the page is reloaded');
    (err as any).telemetryReported = true;
    return err;
  }

  private storageBlockedError(): Error {
    const err = new Error('Local storage is blocked until the page is shown again');
    (err as any).telemetryReported = true;
    return err;
  }

  private async storagePctUsed(): Promise<Record<string, number>> {
    try {
      const {usage = 0, quota = 0} = await navigator.storage?.estimate?.() ?? {};
      return quota ? { storagePctUsed: Math.round(100 * usage / quota) } : {};
    } catch {
      return {};
    }
  }

  private async query<T>(db: Database<DBSchema>, sql: string, params?: Record<string, any>): Promise<T[]> {
    const q = db.createQuery(sql);
    if (params) {
      q.parameters = params;
    }
    return await (q.execute() as Promise<any[]>) as T[];
  }

  private isUsable(db: Database<DBSchema>): boolean {
    return db.isOpen && Object.keys(dbConfig.collections).every(name => !!db.collections[name as keyof DBSchema]);
  }

  private required<C>(collection: C | undefined, name: string): C {
    if (!collection) {
      throw new Error(`[OfflineSync] Database has no collection named '${name}'`);
    }
    return collection;
  }

  async saveDraft<T = Record<string, any>>(
    draftKey: string, meta: DraftMeta, data: T
  ): Promise<void> {
    const cleanData = this.stripFiles(data);

    return this.runWrite('saveDraft', async db => {
      const drafts = this.required(db.collections['drafts'], 'drafts');

      if (this._cleanedFor !== draftKey) {
        await this.cleanUpOldChanges(db, drafts, draftKey);
        this._cleanedFor = draftKey;
      }
      const docId = DocID(this.draftKey(draftKey));
      const existing = await drafts.getDocument(docId);

      const payload: DraftSchema = {
        draftKey,
        draftType: 'draft',
        ...meta,
        data: cleanData,
        savedAt: new Date().toISOString(),
      };

      if (existing) {
        Object.assign(existing, payload);
        await drafts.save(existing, LastWriteWins);
      } else {
        await drafts.save(drafts.createDocument(docId, payload));
      }
    });
  }

  async saveDraftImages(draftKey: string, imageFiles: ImageFile[]): Promise<void> {
    const encoded = this.selectImages(imageFiles);

    return this.runWrite('saveDraftImages', async db => {
      const images = this.required(db.collections['images'], 'images');
      const stored = await this.query<{ docId: string; fieldId: string; fileName: string }>(
        db,
        'SELECT meta().id AS docId, fieldId, fileName FROM images WHERE draftKey = $draftKey',
        { draftKey: this.draftKey(draftKey) }
      );

      const missing = new Map<string, number>();
      for (const img of encoded) {
        const key = this.imageKey(img.questionId, img.fileName);
        missing.set(key, (missing.get(key) ?? 0) + 1);
      }

      for (const row of stored) {
        const key = this.imageKey(row.fieldId, row.fileName);
        const wanted = missing.get(key) ?? 0;
        if (wanted > 0) {
          missing.set(key, wanted - 1);
        } else {
          await images.purge(DocID(row.docId));
        }
      }

      for (const img of encoded) {
        const key = this.imageKey(img.questionId, img.fileName);
        const wanted = missing.get(key) ?? 0;
        if (wanted === 0) {
          continue;
        }
        missing.set(key, wanted - 1);

        const image = await this.toStoredBlob(img);
        if (!image) {
          this.reportUnreadablePhoto('saveDraftImages', draftKey, img);
          continue;
        }

        const payload: ImageSchema = {
          draftKey: this.draftKey(draftKey),
          fieldId: img.questionId,
          fileName: img.fileName,
          mimeType: img.mimeType,
          res: null,
          image,
        };

        const doc = images.createDocument(null, payload);
        await images.save(doc);
      }
    });
  }

  private selectImages(imageFiles: ImageFile[]): PendingImage[] {
    return imageFiles
      .filter(f => f.id == null && f.deleted === false && f.file != null)
      .map(f => ({
        questionId: String((f as any).questionId ?? ''),
        fileName: f.name,
        mimeType: f.file.type || 'image/jpeg',
        file: f.file,
        res: f.res,
      }));
  }

  private async readImageBytes(img: PendingImage): Promise<Uint8Array<ArrayBuffer> | null> {
    try {
      return new Uint8Array(await img.file.arrayBuffer());
    } catch (err) {
      if (typeof img.res === 'string' && img.res.startsWith('data:')) {
        try {
          const bytes = Uint8Array.from(atob(img.res.slice(img.res.indexOf(',') + 1)), c => c.charCodeAt(0));
          this.telemetry.event(TelemetryEvent.OFFLINE_PHOTO_UNREADABLE, LogLevel.WARN, {
            fileName: img.fileName,
            questionId: img.questionId,
            recovered: true,
            errorName: (err as any)?.name,
            error: (err as any)?.message ?? String(err)
          });
          return bytes;
        } catch {
          return null;
        }
      }
      return null;
    }
  }

  private async toStoredBlob(img: PendingImage): Promise<NewBlob | null> {
    const bytes = await this.readImageBytes(img);
    return bytes ? new NewBlob(bytes, img.mimeType) : null;
  }

  private reportUnreadablePhoto(operation: string, draftKey: string, img: PendingImage): void {
    this.telemetry.event(TelemetryEvent.OFFLINE_PHOTO_UNREADABLE, LogLevel.ERROR, {
      operation,
      draftKey,
      fileName: img.fileName,
      questionId: img.questionId,
      recovered: false
    });
  }

  private toDataUrl(blob: Blob, fileName: string): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e: any) => resolve(e.target.result);
      reader.onerror = () => reject(reader.error ?? new Error(`Could not read "${fileName}"`));
      reader.onabort = () => reject(new Error(`Reading "${fileName}" was aborted`));
      reader.readAsDataURL(blob);
    });
  }

  private async purgeOldDraftImages(db: Database<DBSchema>, draftKey: string) {
    const images = this.required(db.collections['images'], 'images');
    const rows = await this.query<{ docId: string }>(
      db,
      'SELECT meta().id AS docId FROM images WHERE draftKey = $draftKey',
      { draftKey: this.draftKey(draftKey) }
    );
    for (const row of rows) {
      await images.purge(DocID(row.docId));
    }
  }

  async getDraftImages(draftKey: string): Promise<SavedImageEntry[]> {
    return this.runRead('getDraftImages', async db => {
      const images = this.required(db.collections['images'], 'images');
      const rows = await this.query<{ docId: string } & ImageSchema>(
        db,
        'SELECT meta().id AS docId, draftKey, fieldId, fileName, mimeType, res, image' +
        ' FROM images WHERE draftKey = $draftKey',
        { draftKey: this.draftKey(draftKey) }
      );

      const result: SavedImageEntry[] = [];
      for (const row of rows) {
        const nativeBlob = await this.toNativeBlob(images, row);

        result.push({
          docId: row.docId,
          fieldId: row.fieldId,
          fileName: row.fileName,
          mimeType: row.mimeType,
          res: await this.toDataUrl(nativeBlob, row.fileName),
          blob: nativeBlob,
        });
      }
      return result;
    });
  }

  async deleteDraft(draftKey: string): Promise<void> {
    return this.runWrite('deleteDraft', async db => {
      await this.purgeDraft(db, draftKey);
      await this.reclaimBlobs(db);
    });
  }

  private async purgeDraft(db: Database<DBSchema>, draftKey: string): Promise<void> {
    const drafts = this.required(db.collections['drafts'], 'drafts');
    const doc = await drafts.getDocument(DocID(this.draftKey(draftKey)));
    if (doc) {
      await drafts.purge(doc);
    }
    await this.purgeOldDraftImages(db, draftKey);
  }

  async listDrafts(): Promise<DraftEntry[]> {
    return this.runRead('listDrafts', async db => {
      const drafts = this.required(db.collections['drafts'], 'drafts');
      const ids = await drafts.documentIDs();
      const result: DraftEntry[] = [];
      for (const id of ids) {
        const doc = await drafts.getDocument(id);
        if (doc) result.push(doc);
      }
      return result;
    });
  }

  private async reclaimBlobs(db: Database<DBSchema>): Promise<void> {
    try {
      await db.performMaintenance('compact');
    } catch (err) {
      console.warn('[OfflineSync] Blob reclamation failed', err);
    }
  }

  public async purgeDraftsOnTerritoryChange(): Promise<void> {
    return this.runWrite('purgeDraftsOnTerritoryChange', async db => {
      await this.purgeCollection(this.required(db.collections['drafts'], 'drafts'));
      await this.purgeCollection(this.required(db.collections['images'], 'images'));
      await this.purgeCollection(this.required(db.collections['pending_photos'], 'pending_photos'));
      await this.reclaimBlobs(db);
      this._cleanedFor = null;
    });
  }

  private async purgeCollection(collection: any): Promise<void> {
    const ids = await collection.documentIDs();

    for (const id of ids) {
      const doc = await collection.getDocument(id);
      if (doc) {
        await collection.purge(doc);
      }
    }
  }

  private stripFiles<T>(data: T): any {
    return JSON.parse(JSON.stringify(data, (key, value) => {
      if (value instanceof File) return undefined;
      return value;
    }));
  }

  private async cleanUpOldChanges(db: Database<DBSchema>, drafts: Collection<DBSchema['drafts']>, draftKey: string) {
    const keep = this.draftKey(draftKey);
    const allDraftIds = await drafts.documentIDs();
    for (const id of allDraftIds) {
      const docId = String(id);
      if (docId === keep) continue;

      if (docId.startsWith(this.DRAFT_PREFIX)) {
        await this.purgeDraft(db, docId.slice(this.DRAFT_PREFIX.length));
      } else {
        await drafts.purge(DocID(docId));
      }
    }
  }

  private draftKey(draftKey: string) {
    return this.DRAFT_PREFIX + draftKey;
  }

  private async toNativeBlob(images: any, row: { docId: string } & ImageSchema): Promise<Blob> {
    if (row.image instanceof CBLBlob) {
      return new Blob([new Uint8Array(await row.image.getContents())], { type: row.mimeType });
    }
    const res = row.res || await this.readResFromRevision(images, row.docId, row.res);
    return this.dataUrlToBlob(res, row);
  }

  private imageKey(questionId: string, fileName: string): string {
    return `${questionId}\u0000${fileName}`;
  }

  private async readResFromRevision(images: any, id: string, fallbackRes: string) {
    try {
      const rev = await images.getStoredRevision(id);
      return rev?.body?.res ?? fallbackRes
    } catch (e) {
      console.error(e);
      return fallbackRes;
    }
  }

  private dataUrlToBlob(res: string, doc: ImageSchema) {
    if (res?.startsWith('data:')) {
      const arr = res.split(',');
      const mime = arr[0].slice(arr[0].indexOf(':') + 1, arr[0].indexOf(';'));
      const bstr = atob(arr[1]);
      const u8arr = new Uint8Array(bstr.length);
      for (let i = 0; i < bstr.length; i++) {
        u8arr[i] = bstr.codePointAt(i);
      }
      return new Blob([u8arr], {type: mime});
    } else {
      const blobData = doc.image;
      return blobData instanceof Blob
        ? blobData
        : new Blob([blobData.bytes ?? blobData], {type: doc.mimeType});
    }
  }
}
